import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Outlet, NavLink, useLocation } from 'react-router-dom';
import Logo from '@/components/Logo';
import ChatWidget from '@/components/ChatWidget';
import NotificationBell from '@/components/NotificationBell';
import { LayoutDashboard, Wrench, FileText, Truck, Plus, LogOut, ChevronDown, User, BarChart3 } from 'lucide-react';

const nav = [
  { to: '/portal/dashboard', icon: LayoutDashboard, label: 'Fleet Overview' },
  { to: '/portal/repairs', icon: Wrench, label: 'Repairs' },
  { to: '/portal/invoices', icon: FileText, label: 'Invoices' },
  { to: '/portal/units', icon: Truck, label: 'My Units' },
  { to: '/portal/analytics', icon: BarChart3, label: 'Analytics' },
];

export default function PortalLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const initials = user?.full_name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() || 'U';

  // Prevent horizontal scroll on PWA/mobile
  useEffect(() => {
    document.body.style.overflowX = 'hidden';
    document.documentElement.style.overflowX = 'hidden';
    return () => {
      document.body.style.overflowX = '';
      document.documentElement.style.overflowX = '';
    };
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMobileMenuOpen(false);
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => { document.removeEventListener('mousedown', handler); document.removeEventListener('touchstart', handler); };
  }, []);

  useEffect(() => { setMobileMenuOpen(false); }, [location.pathname]);

  const currentPage = nav.find(n => location.pathname.startsWith(n.to))?.label || 'Portal';

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg)' }}>

      {/* ═══ DESKTOP TOP BAR (hidden on mobile) ═══ */}
      <nav className="hidden md:block sticky top-0 z-50 backdrop-blur-xl border-b"
        style={{ background: 'rgba(10, 13, 16, 0.95)', borderColor: 'var(--border)' }}>
        <div className="max-w-screen-xl mx-auto px-6 h-14 flex items-center gap-6">
          <div className="flex items-center gap-3 flex-shrink-0">
            <Logo size={28} />
            <span className="font-['Manrope'] font-extrabold text-sm" style={{ color: 'var(--text-primary)' }}>Millennials</span>
            <span className="font-['Manrope'] font-bold text-sm" style={{ color: 'var(--primary)' }}>Trucking</span>
            <div className="flex items-center gap-1.5 ml-2 px-2 py-0.5 rounded-full" style={{ background: 'var(--success-muted)', border: '1px solid var(--success)' }}>
              <div className="w-1.5 h-1.5 rounded-full live-dot" style={{ background: 'var(--success)' }} />
              <span className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-wider" style={{ color: 'var(--success)' }}>Portal</span>
            </div>
          </div>
          <div className="flex items-center gap-0.5 flex-1">
            {nav.map(({ to, icon: Icon, label }) => (
              <NavLink key={to} to={to}
                className="flex items-center gap-2 px-3.5 py-2 rounded-md text-sm transition-all font-['IBM_Plex_Sans'] font-medium"
                style={({ isActive }) => ({
                  color: isActive ? 'var(--primary)' : 'var(--text-muted)',
                  background: isActive ? 'var(--primary-muted)' : 'transparent',
                })}>
                <Icon size={14} />
                <span>{label}</span>
              </NavLink>
            ))}
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            <button onClick={() => navigate('/portal/request-service')}
              className="btn-primary flex items-center gap-2 text-xs uppercase tracking-wide"
              style={{ padding: '0.375rem 1rem' }}
              data-testid="request-service-btn">
              <Plus size={13} /> Request Service
            </button>
            <NotificationBell />
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-md flex items-center justify-center" style={{ background: 'var(--primary-muted)', border: '1px solid var(--primary)' }}>
                <span className="text-xs font-extrabold font-['Manrope']" style={{ color: 'var(--primary)' }}>{initials}</span>
              </div>
              <button onClick={() => { logout(); navigate('/login'); }} className="transition-colors p-1"
                style={{ color: 'var(--text-faint)' }}
                onMouseEnter={e => e.currentTarget.style.color = 'var(--error)'}
                onMouseLeave={e => e.currentTarget.style.color = 'var(--text-faint)'}
                data-testid="portal-logout-button">
                <LogOut size={14} />
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* ═══ MOBILE PWA TOP BAR (hidden on desktop) ═══ */}
      <nav className="md:hidden sticky top-0 z-50 backdrop-blur-xl border-b"
        style={{ background: 'rgba(10, 13, 16, 0.97)', borderColor: 'var(--border)' }}>
        <div className="px-4 h-16 flex items-center justify-between">
          {/* Profile avatar - clickable to edit profile */}
          <button onClick={() => navigate('/portal/profile')}
            className="w-9 h-9 rounded-full flex items-center justify-center transition-transform active:scale-95"
            style={{ background: 'var(--primary-muted)', border: '1px solid var(--primary)' }}
            data-testid="portal-profile-btn">
            <span className="text-xs font-extrabold font-['Manrope']" style={{ color: 'var(--primary)' }}>{initials}</span>
          </button>

          {/* Center logo and dropdown - positioned absolutely to center on screen */}
          <div className="absolute left-1/2 -translate-x-1/2" ref={menuRef}>
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="flex flex-col items-center gap-0.5 active:scale-95 transition-transform"
              data-testid="portal-mobile-menu-trigger">
              <Logo size={48} />
              <div className="flex items-center gap-1">
                <span className="font-['JetBrains_Mono'] text-[10px] uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>{currentPage}</span>
                <ChevronDown size={12} style={{ color: 'var(--text-faint)' }} className={`transition-transform ${mobileMenuOpen ? 'rotate-180' : ''}`} />
              </div>
            </button>

            {mobileMenuOpen && (
              <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-64 rounded-lg overflow-hidden animate-fade-in"
                style={{ background: 'var(--surface)', border: '1px solid var(--border)', boxShadow: '0 16px 48px rgba(0,0,0,0.5)' }}
                data-testid="portal-mobile-dropdown">
                <div className="p-2">
                  {nav.map(({ to, icon: Icon, label }) => (
                    <NavLink key={to} to={to}
                      className="flex items-center gap-3 px-3 py-3 rounded-md transition-all"
                      style={{ color: location.pathname.startsWith(to) ? 'var(--primary)' : 'var(--text-muted)', background: location.pathname.startsWith(to) ? 'var(--primary-muted)' : 'transparent' }}>
                      <Icon size={18} />
                      <span className="font-['IBM_Plex_Sans'] font-medium text-sm">{label}</span>
                      {location.pathname.startsWith(to) && <div className="ml-auto w-1.5 h-1.5 rounded-full" style={{ background: 'var(--primary)' }} />}
                    </NavLink>
                  ))}
                  <NavLink to="/portal/request-service"
                    className="flex items-center gap-3 px-3 py-3 rounded-md transition-all"
                    style={{ color: location.pathname === '/portal/request-service' ? 'var(--primary)' : 'var(--warning)' }}>
                    <Plus size={18} />
                    <span className="font-['IBM_Plex_Sans'] font-medium text-sm">Request Service</span>
                  </NavLink>
                </div>
                <div className="mx-3" style={{ borderTop: '1px solid var(--border)' }} />
                <div className="p-2">
                  <NavLink to="/portal/profile"
                    className="flex items-center gap-3 px-3 py-3 rounded-md transition-all"
                    style={{ color: location.pathname === '/portal/profile' ? 'var(--primary)' : 'var(--text-muted)' }}>
                    <User size={18} />
                    <span className="font-['IBM_Plex_Sans'] font-medium text-sm">Edit Profile</span>
                  </NavLink>
                  <button onClick={() => { logout(); navigate('/login'); }}
                    className="w-full flex items-center gap-3 px-3 py-3 rounded-md transition-all"
                    style={{ color: 'var(--error)' }} data-testid="portal-mobile-logout">
                    <LogOut size={18} />
                    <span className="font-['IBM_Plex_Sans'] font-medium text-sm">Sign Out</span>
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Sign out + Notification */}
          <div className="flex items-center gap-2">
            <button onClick={() => { logout(); navigate('/login'); }}
              className="p-2 rounded-md transition-colors"
              style={{ color: 'var(--text-faint)' }}
              data-testid="mobile-signout-btn">
              <LogOut size={18} />
            </button>
            <NotificationBell />
          </div>
        </div>
      </nav>

      {/* Content */}
      <main className="max-w-screen-xl mx-auto px-4 sm:px-6 py-5 pb-24 md:pb-7">
        <Outlet />
      </main>

      {/* Footer credit — desktop only (mobile bottom nav covers this space) */}
      <footer
        className="hidden md:block max-w-screen-xl mx-auto px-4 sm:px-6 pb-4"
        data-testid="portal-footer-credit"
      >
        <p
          className="text-[10px] text-center"
          style={{ color: 'var(--text-faint)', opacity: 0.7 }}
        >
          © {new Date().getFullYear()} Millennials Maintenance · Designed &amp; built by{' '}
          <a
            href="https://stellardesign.ai/"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:underline"
            style={{ color: 'var(--primary)' }}
          >
            Stellar Design Corp
          </a>
          {' '}· St. Petersburg, FL · Est. 2024
          {' '}·{' '}
          <a href="/USER_MANUAL_SR.pdf" target="_blank" rel="noopener noreferrer"
            className="hover:underline" style={{ color: 'var(--primary)' }} data-testid="portal-manual-link">
            User Manual (PDF)
          </a>
        </p>
      </footer>

      {/* ═══ MOBILE BOTTOM NAV (PWA only) ═══ */}
      <div className="fixed bottom-0 left-0 right-0 z-40 border-t md:hidden"
        style={{ background: 'rgba(10, 13, 16, 0.97)', borderColor: 'var(--border)', backdropFilter: 'blur(16px)' }}
        data-testid="mobile-bottom-nav">
        <div className="flex items-center justify-around h-16 px-2">
          {nav.map(({ to, icon: Icon, label }) => {
            const active = location.pathname.startsWith(to);
            return (
              <NavLink key={to} to={to}
                className="flex flex-col items-center gap-1 py-1 px-3 rounded-lg transition-all"
                style={{ color: active ? 'var(--primary)' : 'var(--text-faint)' }}>
                <Icon size={20} strokeWidth={active ? 2.5 : 1.5} />
                <span className="text-[9px] font-['IBM_Plex_Sans'] font-medium truncate">{label.split(' ').pop()}</span>
                {active && <div className="w-4 h-0.5 rounded-full" style={{ background: 'var(--primary)' }} />}
              </NavLink>
            );
          })}
        </div>
      </div>

      <ChatWidget />
    </div>
  );
}
